import React, { createContext, useState, ReactNode, useContext, useEffect } from 'react';
import { createClient } from '@/util/supabase/component';
import { User, Provider } from '@supabase/supabase-js';
import { useToast } from "@/components/ui/use-toast";
import { useRouter } from 'next/router';

interface AuthContextType {
  user: User | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signInWithMagicLink: (email: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  initializing: boolean;
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  signIn: async () => {},
  signUp: async () => {},
  signInWithMagicLink: async () => {},
  signInWithGoogle: async () => {},
  signOut: async () => {},
  resetPassword: async () => {},
  initializing: true
});

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const router = useRouter();
  const [user, setUser] = useState<User | null>(null);
  const [initializing, setInitializing] = useState(true);
  const supabase = createClient();
  const { toast } = useToast();
  const [refreshTimeout, setRefreshTimeout] = useState<NodeJS.Timeout | null>(null);

  const [lastRefreshAttempt, setLastRefreshAttempt] = useState<number>(0);
  const REFRESH_COOLDOWN = 10000; // 10 seconds cooldown between refresh attempts

  const setupRefreshTimer = async (expiresIn: number) => {
    // Clear any existing timeout
    if (refreshTimeout) {
      clearTimeout(refreshTimeout);
    }

    // Don't setup refresh if expiresIn is invalid or too short
    if (!expiresIn || expiresIn < 60) {
      return;
    }

    // Set up the next refresh at 90% of the token's lifetime
    // This reduces refresh frequency while maintaining security
    const refreshTime = Math.floor(expiresIn * 0.9) * 1000;
    
    const timeout = setTimeout(async () => {
      const now = Date.now();
      
      // Implement exponential backoff for refresh attempts
      if (now - lastRefreshAttempt < REFRESH_COOLDOWN) {
        const nextAttempt = lastRefreshAttempt + REFRESH_COOLDOWN - now;
        setTimeout(() => setupRefreshTimer(expiresIn), nextAttempt);
        return;
      }

      try {
        // Check if session exists and is near expiration
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        if (!currentSession) {
          throw new Error('No active session');
        }

        const currentExpiresIn = Math.floor((currentSession.expires_at || 0) - now / 1000);
        
        // Only refresh if token is actually close to expiring (less than 30 minutes)
        if (currentExpiresIn > 1800) {
          setupRefreshTimer(currentExpiresIn);
          return;
        }

        setLastRefreshAttempt(now);
        const { data: { session }, error } = await supabase.auth.refreshSession();
        if (error) throw error;
        
        if (session) {
          setUser(session.user);
          const newExpiresIn = Math.floor((session.expires_at || 0) - Date.now() / 1000);
          if (newExpiresIn > 0) {
            setupRefreshTimer(newExpiresIn);
          }
        }
      } catch (error) {
        console.error('Failed to refresh session:', error);
        // If refresh fails, wait longer before retrying
        const retryAfter = REFRESH_COOLDOWN * 2;
        setTimeout(() => setupRefreshTimer(expiresIn), retryAfter);
      }
    }, refreshTime);

    setRefreshTimeout(timeout);
  };

  // Store session data in localStorage as a backup
  const storeSessionInLocalStorage = (session: any) => {
    if (!session) return;
    
    try {
      // Store essential session data (not the full session object)
      const sessionData = {
        access_token: session.access_token,
        refresh_token: session.refresh_token,
        expires_at: session.expires_at,
        user_id: session.user.id,
        email: session.user.email,
      };
      
      localStorage.setItem('ensemble_session_backup', JSON.stringify(sessionData));
      localStorage.setItem('ensemble_session_timestamp', Date.now().toString());
    } catch (error) {
      console.error('Failed to store session in localStorage:', error);
    }
  };

  // Try to recover session from localStorage if Supabase cookies are missing
  const recoverSessionFromLocalStorage = async () => {
    try {
      const sessionBackup = localStorage.getItem('ensemble_session_backup');
      const timestamp = localStorage.getItem('ensemble_session_timestamp');
      
      if (!sessionBackup || !timestamp) return null;
      
      // Check if backup is less than 30 days old
      const backupAge = Date.now() - parseInt(timestamp);
      const maxAge = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds
      
      if (backupAge > maxAge) {
        // Clear expired backup
        localStorage.removeItem('ensemble_session_backup');
        localStorage.removeItem('ensemble_session_timestamp');
        return null;
      }
      
      const sessionData = JSON.parse(sessionBackup);
      
      // Try to refresh the session using the stored refresh token
      const { data, error } = await supabase.auth.refreshSession({
        refresh_token: sessionData.refresh_token,
      });
      
      if (error || !data.session) {
        console.error('Failed to recover session:', error);
        return null;
      }
      
      return data.session;
    } catch (error) {
      console.error('Error recovering session from localStorage:', error);
      return null;
    }
  };

  // Initialize auth state
  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        // First try to get the session normally
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) throw error;

        // If we have a valid session, use it
        if (session) {
          if (mounted) {
            setUser(session.user);
            storeSessionInLocalStorage(session);
            const expiresIn = Math.floor((session.expires_at || 0) - Date.now() / 1000);
            if (expiresIn > 0) {
              setupRefreshTimer(expiresIn);
            }
          }
        } else {
          // If no session found, try to recover from localStorage
          console.log('No active session found, attempting recovery from localStorage');
          const recoveredSession = await recoverSessionFromLocalStorage();
          
          if (recoveredSession && mounted) {
            console.log('Successfully recovered session from localStorage');
            setUser(recoveredSession.user);
            storeSessionInLocalStorage(recoveredSession);
            const expiresIn = Math.floor((recoveredSession.expires_at || 0) - Date.now() / 1000);
            if (expiresIn > 0) {
              setupRefreshTimer(expiresIn);
            }
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        
        // Try recovery as a last resort
        try {
          const recoveredSession = await recoverSessionFromLocalStorage();
          if (recoveredSession && mounted) {
            console.log('Recovered session after initialization error');
            setUser(recoveredSession.user);
            const expiresIn = Math.floor((recoveredSession.expires_at || 0) - Date.now() / 1000);
            if (expiresIn > 0) {
              setupRefreshTimer(expiresIn);
            }
          } else if (mounted) {
            setUser(null);
          }
        } catch (recoveryError) {
          console.error('Recovery attempt failed:', recoveryError);
          if (mounted) setUser(null);
        }
      } finally {
        if (mounted) setInitializing(false);
      }
    };

    initializeAuth();

    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (mounted) {
        if (session) {
          setUser(session.user);
          storeSessionInLocalStorage(session);
          
          if (session.expires_at) {
            const expiresIn = Math.floor(session.expires_at - Date.now() / 1000);
            if (expiresIn > 0) {
              setupRefreshTimer(expiresIn);
            }
          }
        } else {
          setUser(null);
        }
        
        setInitializing(false);
      }
    });

    return () => {
      mounted = false;
      if (refreshTimeout) {
        clearTimeout(refreshTimeout);
      }
      authListener.subscription.unsubscribe();
    };
  }, []);

  const checkProfile = async (userId: string) => {
    try {
      const response = await fetch('/api/profile');
      const data = await response.json();
      return !!data?.fullName;
    } catch (error) {
      return false;
    }
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    
    if (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
      throw error;
    } else {
      // Store session in localStorage immediately for better persistence
      if (data.session) {
        storeSessionInLocalStorage(data.session);
      }
      
      const hasProfile = await checkProfile(data.user.id);
      
      if (!hasProfile) {
        toast({
          title: "Welcome!",
          description: "Please complete your profile information.",
        });
        router.push('/profile');
      } else {
        toast({
          title: "Success",
          description: "You have successfully signed in",
        });
        // Note: We don't redirect here anymore as the login page will handle the redirect
      }
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/email-verified`,
        }
      });

      if (error) throw error;

      if (!data.user) {
        throw new Error('No user data returned from signup');
      }

      try {
        const response = await fetch('/api/auth/user', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          }
        });

        if (!response.ok) {
          console.error('User profile creation error:', await response.text());
        }
      } catch (profileError) {
        console.error('Profile creation error:', profileError);
      }

      toast({
        title: "Welcome!",
        description: "Please check your email to verify your account.",
      });
      
      return data;
    } catch (error: any) {
      console.error('Signup process error:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to complete signup process",
      });
      throw error;
    }
  };

  const signInWithMagicLink = async (email: string) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: false,
        emailRedirectTo: `${window.location.origin}/events`,
      },
    });

    if (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
      throw error;
    } else {
      toast({
        title: "Success",
        description: "Check your email for the login link",
      });
    }
  };

  const signInWithGoogle = async () => {
    try {
      const redirectUrl = process.env.NEXT_PUBLIC_APP_URL 
        ? `${process.env.NEXT_PUBLIC_APP_URL}/auth/callback`
        : `${window.location.origin}/auth/callback`;

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google' as Provider,
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
          skipBrowserRedirect: true // This prevents automatic redirect
        }
      });
      
      if (error) {
        console.error('Google sign in error:', error);
        toast({
          variant: "destructive",
          title: "Sign in failed",
          description: "Could not sign in with Google. Please try again.",
        });
        return;
      }

      // If we have a URL, open it in a new window/tab
      if (data?.url) {
        window.open(data.url, '_self');
      }
    } catch (error: any) {
      console.error('Google sign in error:', error);
      toast({
        variant: "destructive",
        title: "Sign in failed",
        description: "Could not sign in with Google. Please try again.",
      });
    }
  };

  const signOut = async () => {
    try {
      if (refreshTimeout) {
        clearTimeout(refreshTimeout);
        setRefreshTimeout(null);
      }

      const { error } = await supabase.auth.signOut();
      
      // Force clear all session data
      window.localStorage.removeItem('sb-' + process.env.NEXT_PUBLIC_SUPABASE_URL + '-auth-token');
      window.localStorage.removeItem('supabase.auth.token');
      window.localStorage.removeItem('supabase.auth.expires_at');
      window.localStorage.removeItem('supabase.auth.refresh_token');
      
      // Clear our custom session backup
      window.localStorage.removeItem('ensemble_session_backup');
      window.localStorage.removeItem('ensemble_session_timestamp');
      
      setUser(null);
      
      toast({
        title: "Signed out",
        description: "You have been successfully signed out",
      });
      
      setTimeout(() => {
        router.push('/');
      }, 100);
      
    } catch (error: any) {
      // Force cleanup even on error
      if (refreshTimeout) {
        clearTimeout(refreshTimeout);
        setRefreshTimeout(null);
      }
      
      // Clear all session data
      window.localStorage.removeItem('sb-' + process.env.NEXT_PUBLIC_SUPABASE_URL + '-auth-token');
      window.localStorage.removeItem('supabase.auth.token');
      window.localStorage.removeItem('supabase.auth.expires_at');
      window.localStorage.removeItem('supabase.auth.refresh_token');
      
      // Clear our custom session backup
      window.localStorage.removeItem('ensemble_session_backup');
      window.localStorage.removeItem('ensemble_session_timestamp');
      
      setUser(null);
      
      toast({
        title: "Signed out",
        description: "You have been successfully signed out",
      });
      
      setTimeout(() => {
        router.push('/');
      }, 100);
    }
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
      throw error;
    } else {
      toast({
        title: "Success",
        description: "Check your email for the password reset link",
      });
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      signIn,
      signUp,
      signInWithMagicLink,
      signInWithGoogle,
      signOut,
      resetPassword,
      initializing,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);